/**
 * "Comment on GitLab" on an element's own page - issue #99, phase C.
 *
 * WHY IT LIVES ON THE ELEMENT'S PAGE. A GitLab diff comment belongs to a merge
 * request and is pinned to a line the request's diff can place. An element with
 * no change near it still has a declaration line, and this is the one place that
 * can comment on it. The change list's comment button leads here too (same
 * route, `#comment`), so there is one component and one place a thread is made.
 *
 * BEST READS AND FORWARDS; GITLAB ENFORCES. The threads are GitLab's, shown as
 * they are and listed through the same route and drawn with the same pieces as
 * the Version Control screen's discussion panel. Who sees the box is the
 * screen's writer check (`canComment`); a refusal is GitLab's own words in a
 * toast, never "sign in again".
 *
 * NO OPEN MERGE REQUEST means nothing to post to, and the section says so rather
 * than hiding: the person is looking for the box, and a missing one reads as a
 * fault.
 *
 * A comment GitLab would not place on a line is posted as an ordinary thread
 * and the server says so in `notice`. That is SHOWN, in the section, not only
 * toasted: the person asked for a comment on a line and got one that is not.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type { DiscussionList, DiscussionThread, MergeRequest } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { Thread } from './DiscussionPanel'
import { MAX_COMMENT } from './discussions'
import { COMMENT_HASH, COMMENT_ID, NO_MERGE_REQUEST } from './elementThreads'

export default function ElementComments({
  slug,
  branch,
  element,
  canComment,
  reason = '',
}: {
  slug: string
  /** The branch being viewed; sent as the ref so the comment lands where the person is looking. */
  branch: string
  /**
   * The id the element's page is addressed by. The server resolves the element
   * from it, both to post and to list: only this element's threads come back.
   */
  element: string
  /** The screen's writer check. Without it the threads are read-only. */
  canComment: boolean
  /** Why the person cannot comment, shown instead of the box. */
  reason?: string
}) {
  const toasts = useToasts()
  const { hash } = useLocation()
  const section = useRef<HTMLDivElement | null>(null)
  const box = useRef<HTMLTextAreaElement | null>(null)

  const [open, setOpen] = useState<MergeRequest | null>(null)
  const [list, setList] = useState<DiscussionList | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState('')
  const [notice, setNotice] = useState('')

  // The ref for every call: empty means "the branch BEST remembers", which is
  // the same branch, and leaving it out keeps the request what it always was.
  const ref = branch || undefined

  /**
   * The merge request open on this branch, then its threads.
   *
   * Two reads because they are two routes with two ways to fail. A 409 from the
   * first is an ordinary state ("nothing to merge yet"), not a fault, and means
   * there is nothing to comment on.
   */
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const state = await api.mergeState(slug)
      setOpen(state.open)
      if (!state.open) {
        setList(null)
        setError(null)
        return
      }
      const listed = await api.listDiscussions(slug, branch, element)
      setList(listed)
      setError(null)
    } catch (failure) {
      const refused = failure as ApiError
      if (refused.status === 409) {
        setOpen(null)
        setList(null)
        setError(null)
      } else {
        setError(refused)
      }
    } finally {
      setLoading(false)
    }
  }, [slug, branch, element])

  useEffect(() => {
    void load()
  }, [load])

  // Arriving from the change list's button (`#comment`): bring the section into
  // view and put the cursor in the box. Waits for the load, because the box is
  // not there before it and the section moves as the threads arrive.
  // Once only: a re-read after posting must not drag the page back up.
  const arrived = useRef(false)
  const ready = !loading
  useEffect(() => {
    if (hash !== COMMENT_HASH || !ready || arrived.current) return
    arrived.current = true
    section.current?.scrollIntoView?.({ block: 'start' })
    box.current?.focus()
  }, [hash, ready])

  const write = async (act: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await act()
      toasts.succeeded(done)
      await load()
      return true
    } catch (failure) {
      const refused = failure as ApiError
      toasts.failed(refused.message || 'GitLab refused')
      if (refused.status === 403 || refused.status === 409) void load()
      return false
    } finally {
      setBusy(false)
    }
  }

  const post = async () => {
    setBusy(true)
    try {
      const result = await api.postElementComment(slug, { element, body: fresh, ref })
      setFresh('')
      setNotice(result.notice || '')
      toasts.succeeded(result.anchored ? 'Comment posted on the line' : 'Comment posted')
      await load()
    } catch (failure) {
      // The text stays in the box: a refused comment is not lost.
      const refused = failure as ApiError
      toasts.failed(refused.message || 'GitLab refused')
      if (refused.status === 403 || refused.status === 409) void load()
    } finally {
      setBusy(false)
    }
  }

  const reply = (thread: DiscussionThread, body: string) =>
    write(() => api.replyToDiscussion(slug, thread.id, { body, ref }), 'Reply posted')
  const resolve = (thread: DiscussionThread) =>
    void write(
      () => api.resolveDiscussion(slug, thread.id, { resolved: !thread.resolved, ref }),
      thread.resolved ? 'Discussion reopened' : 'Discussion resolved',
    )

  const threads = list?.threads ?? []

  return (
    <Paper sx={{ p: 2 }} id={COMMENT_ID} ref={section} data-testid="element-comments">
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          Comment on GitLab
        </Typography>
        {loading ? <CircularProgress size={16} aria-label="Loading the comments" /> : null}
      </Stack>

      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Could not read the merge request: {error.message}
        </Alert>
      ) : null}

      {!loading && !error && !open ? (
        <Typography variant="body2" color="text.secondary">
          {NO_MERGE_REQUEST}{' '}
          <Link component={RouterLink} to="/version-control">
            Go to Version Control
          </Link>
        </Typography>
      ) : null}

      {open ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            On{' '}
            <Link href={open.web_url} target="_blank" rel="noreferrer">
              merge request !{open.iid}
            </Link>
            . A comment here is pinned to this element's lines in its diff.
          </Typography>

          <Stack spacing={1.5}>
            {list && threads.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No comments on this element yet.
              </Typography>
            ) : null}
            {threads.map((thread, index) => (
              <Thread
                key={thread.id}
                thread={thread}
                number={index + 1}
                canComment={canComment}
                busy={busy}
                onReply={reply}
                onResolve={resolve}
              />
            ))}
          </Stack>

          {notice ? (
            <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setNotice('')}>
              {notice}
            </Alert>
          ) : null}

          {canComment ? (
            <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={10}
                label="New comment"
                value={fresh}
                onChange={(event) => setFresh(event.target.value)}
                disabled={busy}
                inputRef={box}
                slotProps={{ htmlInput: { maxLength: MAX_COMMENT } }}
              />
              <Button
                variant="contained"
                disabled={busy || !fresh.trim()}
                onClick={() => void post()}
              >
                Comment
              </Button>
            </Stack>
          ) : reason ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {reason}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Paper>
  )
}
